import { Button, Heading, Text } from '@lmc/ui-kit/src/general'
import { Image } from 'antd'
import useHomeType from 'hooks/useHomeType'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import alarmClockIcon from './images/Alarm-Clock--Streamline-Core.svg'
import paywallBackground1 from './images/paywall-background-1.svg'
import paywallBackground2 from './images/paywall-background-2.svg'
import paywallBackground3 from './images/paywall-background-3.svg'
import rotaAllocationWindow from './images/rota-allocation-window.svg'
import rotaMonitoring from './images/rota-monitoring.svg'
import rotaOverviewReport from './images/rota-overview-report.svg'
import RotaPaywallModal from './RotaPaywallModal'

const RotaPaywall = () => {
  const { isMultiSiteHome, alreadyHadRotaTrialEnabled } = useHomeType()

  const [showModal, setShowModal] = useState(false)

  const { t } = useTranslation()

  const handleCTAClick = () => {
    if (alreadyHadRotaTrialEnabled) {
      // Show survey as overlay on the same page
      if (window.Intercom) {
        window.Intercom('startSurvey', 49526300)
      }
      return
    }

    if (isMultiSiteHome) {
      window.open(
        'https://forms.office.com/Pages/ResponsePage.aspx?id=ESJ6XT3cEEy2Wj1oOCo8toirePpKoE9Hn6sqTeMpScxUMDAxTkVTNkdCRkNaSkdXWUNGWVNTWFczMi4u',
        '_blank'
      )
    } else {
      setShowModal(true)
    }
  }

  return (
    <Container>
      <TitleFrame>
        {alreadyHadRotaTrialEnabled ? (
          <>
            <PurpleHeading level={1}>{t('TRIAL_ENDED')}</PurpleHeading>
            <PurpleText>{t('LET_US_KNOW')}</PurpleText>
            <EndTrialDescriptionText level={1.5}>
              {t('LEAVE_FEEDBACK')}
            </EndTrialDescriptionText>
            <StyledButton variant="primary" onClick={handleCTAClick}>
              <PurpleHeading level={5} weight="bold">
                {t('ADD_ROSTERING_TO_YOUR_PLAN')}
              </PurpleHeading>
            </StyledButton>
          </>
        ) : (
          <>
            <PurpleHeading level={1}>{t('ROTA_PAYWALL_TITLE')}</PurpleHeading>
            <StyledButton variant="primary" onClick={handleCTAClick}>
              <PurpleHeading level={5} weight="bold">
                {t('ROTA_PAYWALL_CTA_1')}
              </PurpleHeading>
            </StyledButton>
            <PinkSection>
              <Header>
                <AlarmIcon src={alarmClockIcon} alt="Alarm Clock" />
                <Heading level={3} weight="medium">
                  {t('LIMITED_TIME_OFFER')}
                </Heading>
              </Header>
              <Text>
                <Trans
                  i18nKey="START_TRIAL_OFFER_1"
                  components={{ strong: <strong /> }}
                />
              </Text>
            </PinkSection>
          </>
        )}
      </TitleFrame>

      <DescriptionFrame>
        {alreadyHadRotaTrialEnabled ? (
          <>
            <EndTrialDescriptionInfo>
              <EndTrialPinkHeading level={3} weight="medium">
                {t('BEST_ROTA_MANAGEMENT')}
              </EndTrialPinkHeading>
              <ItalicText>{t('ONE_SYSTEM')}</ItalicText>
            </EndTrialDescriptionInfo>
            <FeaturesList>
              <FeatureItem>
                <FeatureDot />
                <Text>{t('END_TRIAL_ROTA_MANAGEMENT')}</Text>
              </FeatureItem>
              <FeatureItem>
                <FeatureDot />
                <Text>{t('END_TRIAL_CLOCK_IN')}</Text>
              </FeatureItem>
              <FeatureItem>
                <FeatureDot />
                <Text>{t('END_TRIAL_MONITORING')}</Text>
              </FeatureItem>
            </FeaturesList>
            <StyledButton variant="primary" onClick={handleCTAClick}>
              <PurpleHeading level={5} weight="bold">
                {t('ADD_ROSTERING_TO_YOUR_PLAN')}
              </PurpleHeading>
            </StyledButton>
          </>
        ) : (
          <>
            <FeaturesList>
              <FeatureItem>
                <FeatureDot />
                <Text>{t('ROTA_FEATURE_MATCHING')}</Text>
              </FeatureItem>
              <FeatureItem>
                <FeatureDot />
                <Text>{t('ROTA_FEATURE_CARER_APP')}</Text>
              </FeatureItem>
              <FeatureItem>
                <FeatureDot />
                <Text>{t('ROTA_FEATURE_MONITORING')}</Text>
              </FeatureItem>
              <FeatureItem>
                <FeatureDot />
                <Text>{t('ROTA_FEATURE_EXPORT')}</Text>
              </FeatureItem>
            </FeaturesList>
            <DescriptionInfo>
              <PinkHeading level={3} weight="medium">
                {t('DESCRIPTION_TITLE')}
              </PinkHeading>
              <StyledButton variant="primary" onClick={handleCTAClick}>
                <PurpleHeading level={5} weight="bold">
                  {t('ROTA_PAYWALL_CTA_2')}
                </PurpleHeading>
              </StyledButton>
              <LightText level={2}>{t('ROTA_PRICING')}</LightText>
            </DescriptionInfo>
          </>
        )}
      </DescriptionFrame>

      {!alreadyHadRotaTrialEnabled && (
        <VideoFrame>
          <StyledIframe
            src="https://www.youtube.com/embed/ZM8Dz0b8vi0"
            title="YouTube rostering video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </VideoFrame>
      )}

      <ProductPresentationFrame>
        <RowContainer>
          <TextContainer>
            <PurpleHeading level={3} weight="medium">
              {t('PERFECT_MATCH')}
            </PurpleHeading>
            <PurpleText>{t('PERFECT_MATCH_DESCRIPTION_1')}</PurpleText>
            <PurpleText>{t('PERFECT_MATCH_DESCRIPTION_2')}</PurpleText>
          </TextContainer>
          <ImageContainer>
            <Image
              src={rotaAllocationWindow}
              alt="Rota allocation window"
              width="100%"
              preview={false}
            />
          </ImageContainer>
        </RowContainer>
        <RowContainer>
          <ImageContainer>
            <Image
              src={rotaMonitoring}
              alt="Rota allocation window"
              width="100%"
              preview={false}
            />
          </ImageContainer>
          <TextContainer>
            <PurpleHeading level={3} weight="medium">
              {t('CONFIDENCE_IN_TEAM')}
            </PurpleHeading>
            <PurpleText>{t('CONFIDENCE_IN_TEAM_DESCRIPTION_1')}</PurpleText>
            <PurpleText>{t('CONFIDENCE_IN_TEAM_DESCRIPTION_2')}</PurpleText>
          </TextContainer>
        </RowContainer>
        <RowContainer>
          <TextContainer>
            <PurpleHeading level={3} weight="medium">
              {t('TOOLS_FOR_PAYROLLS')}
            </PurpleHeading>
            <PurpleText>{t('TOOLS_FOR_PAYROLLS_DESCRIPTION_1')}</PurpleText>
            <PurpleText>{t('TOOLS_FOR_PAYROLLS_DESCRIPTION_2')}</PurpleText>
          </TextContainer>
          <ImageContainer>
            <Image
              src={rotaOverviewReport}
              alt="Rota overview report"
              width="100%"
              preview={false}
            />
          </ImageContainer>
        </RowContainer>
      </ProductPresentationFrame>

      {!alreadyHadRotaTrialEnabled && (
        <EndingFrame>
          <StyledButton variant="primary" onClick={handleCTAClick}>
            <PurpleHeading level={5} weight="bold">
              {t('ROTA_PAYWALL_CTA_3')}
            </PurpleHeading>
          </StyledButton>
          <PinkSection2>
            <Header>
              <AlarmIcon src={alarmClockIcon} alt="Alarm Clock" />
              <Heading level={3} weight="medium">
                {t('LIMITED_TIME_OFFER')}
              </Heading>
            </Header>
            <Text>
              <Trans
                i18nKey="START_TRIAL_OFFER_1"
                components={{ strong: <strong /> }}
              />
            </Text>
          </PinkSection2>
          <SmallPrintContainer>
            <PurpleText level={2}>{t('ROTA_PRICING')}</PurpleText>
          </SmallPrintContainer>
        </EndingFrame>
      )}

      {showModal && (
        <RotaPaywallModal showPrompt={showModal} setShowPrompt={setShowModal} />
      )}
    </Container>
  )
}

export default RotaPaywall

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TitleFrame = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  background-color: ${({ theme }) => theme.color.neutral.color11};
  background-image: url(${paywallBackground1});
  background-repeat: no-repeat;
  background-position: right bottom;
  border-radius: 32px;
  padding: 40px 80px;
`

const PurpleHeading = styled(Heading)`
  color: ${({ theme }) => theme.palette.darkpurple};
`

const StyledButton = styled(Button)`
  border-radius: 32px;
  padding: 12px 16px;
  width: fit-content;
  color: ${({ theme }) => theme.palette.darkpurple};
  background-color: #fc0;

  &:hover {
    background-color: #ffdd00;
  }
`
const PinkSection = styled.div`
  background-color: #f09;
  color: ${({ theme }) => theme.color.neutral.color11};
  padding: 24px;
  border-radius: 24px;
  width: 80%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const AlarmIcon = styled.img`
  width: 32px;
  height: 32px;
`

const StyledIframe = styled.iframe`
  width: 100%;
  aspect-ratio: 16/9;
  border-radius: 16px;
`
const VideoFrame = styled.div`
  border-radius: 32px;
  padding: 40px 80px;
  background-color: #ccf;
`
const DescriptionFrame = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
  background-color: ${({ theme }) => theme.color.neutral.color11};
  background-image: url(${paywallBackground2});
  background-repeat: no-repeat;
  background-position: left top;
  background-size: contain;
  border-radius: 32px;
  padding: 40px 80px;
  text-align: justify;
`
const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: ${({ theme }) => theme.palette.darkpurple};
  width: 60%;
`

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

const FeatureDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #f09;
  flex-shrink: 0;
  margin-top: 8px;
`
const PinkHeading = styled(Heading)`
  color: #f09;
  width: 80%;
  text-align: center;
`
const DescriptionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
`
const LightText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
  width: 55%;
  text-align: center;
`
const ProductPresentationFrame = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
  background-color: ${({ theme }) => theme.color.neutral.color11};
  border-radius: 32px;
  padding: 40px 80px;
  text-align: justify;
`
const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 100px;
`
const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`
const ImageContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`
const PurpleText = styled(Text)`
  color: ${({ theme }) => theme.palette.darkpurple};
`
const EndingFrame = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  background-color: ${({ theme }) => theme.color.neutral.color11};
  background-image: url(${paywallBackground3});
  background-repeat: no-repeat;
  background-position: left -20px;
  background-size: cover;
  border-radius: 32px;
  padding: 40px 80px;
  text-align: justify;
`
const PinkSection2 = styled(PinkSection)`
  width: 70%;
  text-align: center;
  align-items: center;
`
const SmallPrintContainer = styled.div`
  width: 70%;
  text-align: center;
`
const EndTrialDescriptionInfo = styled(DescriptionInfo)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
`
const EndTrialPinkHeading = styled(PinkHeading)`
  width: 75%;
`
const ItalicText = styled(PurpleText)`
  font-style: italic;
`
const EndTrialDescriptionText = styled(PurpleText)`
  width: 80%;
`
